import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment, useMemo, useState } from 'react';

import { Position, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useIsVisibleInViewport from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import usePrevious from '@sorare/core/src/hooks/usePrevious';

import { HotStreak } from 'components/forYou/HotStreak';
import { Popular } from 'components/forYou/Popular';
import { WinningTeams } from 'components/forYou/WinningTeams';

import { FriendsPurchases } from '../FriendsPurchases';
import { Substitutes } from '../Substitutes';
import {
  ForYouRowsQuery,
  ForYouRowsQueryVariables,
} from './__generated__/index.graphql';

const FOR_YOU_ROWS_QUERY = gql`
  query ForYouRowsQuery(
    $so5LeagueSlug: String!
    $sport: Sport!
    $positions: [Position!]!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
  ) {
    currentUser {
      slug
    }
    so5 {
      so5League(slug: $so5LeagueSlug) {
        slug
        ...HotStreak_so5League
        ...Popular_so5League
      }
    }
    forYou {
      winningLineup(so5LeagueSlug: $so5LeagueSlug) {
        ...WinningTeams_winningLineup
      }
    }
  }
  ${HotStreak.fragments.so5League}
  ${Popular.fragments.so5League}
  ${WinningTeams.fragments.winningLineup}
` as TypedDocumentNode<ForYouRowsQuery, ForYouRowsQueryVariables>;

enum Heuristic {
  HOT_STREAK = 'hot-streak',
  WINNING_TEAMS = 'winning-teams',
  POPULAR = 'popular',
  FRIENDS_PURCHASES = 'friends-purchases',
  SUBSTITUTES = 'substitutes',
}

const heuristics = [
  Heuristic.HOT_STREAK,
  Heuristic.WINNING_TEAMS,
  Heuristic.POPULAR,
  Heuristic.FRIENDS_PURCHASES,
  Heuristic.SUBSTITUTES,
];

type Props = {
  so5LeagueSlug: string;
  upcomingSo5LeagueSlug?: string;
  sport: Sport;
  positions: Position[];
  onStatsClick: (slug: string) => void;
};

export const Rows = ({
  so5LeagueSlug,
  upcomingSo5LeagueSlug,
  sport,
  positions,
  onStatsClick,
}: Props) => {
  const [skipFriendsPurchase, setSkipFriendsPurchases] = useState(true);
  const previousSo5LeagueSlug = usePrevious(so5LeagueSlug);
  const { handleRef, isVisible } = useIsVisibleInViewport();
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const { data, loading } = useQuery(FOR_YOU_ROWS_QUERY, {
    variables: {
      so5LeagueSlug,
      sport,
      positions,
      isIosApp,
      isAndroidApp,
      googlePlayStoreCountryCode,
    },
  });

  const winningLineup = data?.forYou?.winningLineup;
  const so5League = data?.so5?.so5League;

  if (
    previousSo5LeagueSlug &&
    previousSo5LeagueSlug !== so5LeagueSlug &&
    !skipFriendsPurchase &&
    !isVisible
  ) {
    setSkipFriendsPurchases(true);
  }

  if (isVisible && skipFriendsPurchase) {
    setSkipFriendsPurchases(false);
  }

  const blocks = {
    [Heuristic.HOT_STREAK]: (
      <HotStreak
        onStatsClick={onStatsClick}
        so5League={so5League}
        loading={loading}
        so5LeagueSlug={so5LeagueSlug}
      />
    ),
    [Heuristic.WINNING_TEAMS]: (
      <WinningTeams
        onStatsClick={onStatsClick}
        winningLineup={winningLineup}
        loading={loading}
        so5LeagueSlug={so5LeagueSlug}
        sport={sport}
      />
    ),
    [Heuristic.POPULAR]: (
      <Popular
        so5League={so5League}
        onStatsClick={onStatsClick}
        loading={loading}
        so5LeagueSlug={so5LeagueSlug}
      />
    ),
    [Heuristic.FRIENDS_PURCHASES]: (
      <FriendsPurchases
        skip={skipFriendsPurchase}
        onStatsClick={onStatsClick}
        so5LeagueSlug={so5LeagueSlug}
      />
    ),
    [Heuristic.SUBSTITUTES]: (
      <Substitutes
        onStatsClick={onStatsClick}
        so5LeagueSlug={upcomingSo5LeagueSlug}
      />
    ),
  };

  const shuffledHeuristics = useMemo(
    () =>
      heuristics
        .map(value => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value),
    []
  );

  const friendRecentPurchaseTriggerIndex =
    shuffledHeuristics.findIndex(h => h === Heuristic.FRIENDS_PURCHASES) - 1;

  return (
    <Vertical gap={2}>
      {friendRecentPurchaseTriggerIndex === -1 && <div ref={handleRef} />}
      {...shuffledHeuristics.map((h, i) => {
        return (
          <Fragment key={h}>
            {i === friendRecentPurchaseTriggerIndex && <div ref={handleRef} />}
            {blocks[h]}
          </Fragment>
        );
      })}
    </Vertical>
  );
};

export default Rows;
